import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  FaTimes,
  FaUndo,
  FaReply,
  FaArrowRight,
  FaPaperclip,
  FaHistory,
  FaSpinner,
} from 'react-icons/fa';

/**
 * TimelineRevisoes
 * ----------------------------------------------------------------------------
 * Linha do tempo do ciclo de devolução/correção de uma prestação.
 * Mostra, em ordem cronológica, cada evento:
 *   - devolução (gestor)  → motivo da recusa
 *   - correção (motorista) → justificativa + valor antes→depois + troca de anexo
 *
 * Fonte: GET /api/gastos/:id/revisoes  → { prestacao, revisoes[] }
 *
 * Props:
 *   gastoId   (number)  — id da despesa/prestação
 *   descricao (string)  — descrição limpa, para o cabeçalho (opcional)
 *   onClose   (fn)      — fecha o modal
 */

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return Number.isNaN(num)
    ? 'R$ 0,00'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// Conta quantos anexos há num anexo_url (JSON array serializado ou id único).
const contarAnexos = (raw) => {
  if (!raw) return 0;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.length;
    return 1;
  } catch {
    return String(raw).trim() ? 1 : 0;
  }
};

export default function TimelineRevisoes({ gastoId, descricao, onClose }) {
  const [loading, setLoading]   = useState(true);
  const [erro, setErro]         = useState('');
  const [prestacao, setPrestacao] = useState(null);
  const [revisoes, setRevisoes] = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get(`/api/gastos/${gastoId}/revisoes`);
      setPrestacao(data.prestacao || null);
      setRevisoes(Array.isArray(data.revisoes) ? data.revisoes : []);
    } catch {
      setErro('Não foi possível carregar o histórico de revisões.');
    } finally {
      setLoading(false);
    }
  }, [gastoId]);

  useEffect(() => { carregar(); }, [carregar]);

  // Fecha com ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cgrev-overlay" onMouseDown={onClose}>
      <div
        className="cgrev-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Histórico de revisões da prestação"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="cgrev-head">
          <div className="cgrev-head__title">
            <span className="cgrev-head__icon"><FaHistory /></span>
            <div>
              <h3>Histórico de revisões</h3>
              {descricao && <p title={descricao}>{descricao}</p>}
            </div>
          </div>
          <button className="cgrev-close" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        </div>

        {/* Resumo do estado atual */}
        {prestacao && !loading && (
          <div className="cgrev-summary">
            <div className="cgrev-summary__item">
              <span>Ciclos</span>
              <strong>{prestacao.ciclos_revisao || 0}</strong>
            </div>
            {prestacao.valor_devolvido != null && (
              <div className="cgrev-summary__item">
                <span>Valor na devolução</span>
                <strong>{formatCurrency(prestacao.valor_devolvido)}</strong>
              </div>
            )}
            <div className="cgrev-summary__item">
              <span>Valor atual</span>
              <strong>{formatCurrency(prestacao.valor)}</strong>
            </div>
          </div>
        )}

        {/* Corpo */}
        <div className="cgrev-body">
          {loading ? (
            <div className="cgrev-state">
              <FaSpinner className="cgrev-spin" /> Carregando histórico…
            </div>
          ) : erro ? (
            <div className="cgrev-state cgrev-state--error">
              {erro}
              <button className="cgrev-retry" onClick={carregar}>Tentar de novo</button>
            </div>
          ) : revisoes.length === 0 ? (
            <div className="cgrev-state">
              Esta prestação ainda não passou por nenhuma devolução.
            </div>
          ) : (
            <ul className="cgrev-timeline">
              {revisoes.map((r) => {
                const isDevolucao = r.evento === 'devolucao';
                const anexoMudou =
                  (r.anexo_antes || null) !== (r.anexo_depois || null);
                return (
                  <li
                    key={r.id}
                    className={`cgrev-item ${isDevolucao ? 'is-devolucao' : 'is-correcao'}`}
                  >
                    <span className="cgrev-item__dot">
                      {isDevolucao ? <FaUndo /> : <FaReply />}
                    </span>

                    <div className="cgrev-item__card">
                      <div className="cgrev-item__top">
                        <span className="cgrev-item__label">
                          {isDevolucao ? 'Devolvida pelo gestor' : 'Corrigida pelo motorista'}
                        </span>
                        <span className="cgrev-item__meta">
                          {r.autor_nome ? `${r.autor_nome} · ` : ''}
                          {formatDateTime(r.criado_em)}
                          {r.ciclo ? ` · ciclo ${r.ciclo}` : ''}
                        </span>
                      </div>

                      {/* Texto: motivo (devolução) ou justificativa (correção) */}
                      {isDevolucao ? (
                        r.motivo && <p className="cgrev-item__text">{r.motivo}</p>
                      ) : (
                        r.justificativa && <p className="cgrev-item__text">{r.justificativa}</p>
                      )}

                      {/* Mudança de valor (só na correção, quando alterou) */}
                      {!isDevolucao && Number(r.valor_alterado) === 1 && (
                        <div className="cgrev-item__valor">
                          <span className="cgrev-valor__old">{formatCurrency(r.valor_antes)}</span>
                          <FaArrowRight className="cgrev-valor__arrow" />
                          <span className="cgrev-valor__new">{formatCurrency(r.valor_depois)}</span>
                          <span className="cgrev-valor__tag">valor corrigido</span>
                        </div>
                      )}

                      {/* Troca de comprovante */}
                      {!isDevolucao && anexoMudou && (
                        <div className="cgrev-item__anexo">
                          <FaPaperclip />
                          Comprovante atualizado
                          {contarAnexos(r.anexo_depois) > 0 &&
                            ` (${contarAnexos(r.anexo_depois)} arquivo${contarAnexos(r.anexo_depois) !== 1 ? 's' : ''})`}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}